import { getBaseUrl } from './baseUrl';

export type PlatformUserActivityStatus = 'ACTIVE' | 'INACTIVE' | 'ENDED';

export type PlatformUserActivitySession = {
  id: string;
  user: { id: string; username: string; fullName: string | null; email: string | null };
  organization: { id: string; name: string | null; slug: string | null };
  roleKey: string | null;
  scopeType: 'UNIT' | 'GROUP';
  licenseNumber: string | null;
  licenseProductCode: string | null;
  licenseAccessType: string | null;
  location: { ipAddress: string | null; countryCode: string | null; region: string | null; city: string | null; timezone: string | null };
  device: { type: string | null; browser: string | null; os: string | null };
  startedAt: string;
  lastSeenAt: string;
  lastActiveAt: string | null;
  endedAt: string | null;
  endReason: string | null;
  durationSeconds: number;
  activeSeconds: number;
  status: PlatformUserActivityStatus;
  currentPage: { surface: string; key: string; label: string | null } | null;
  pageVisitCount?: number;
};

export type PlatformUserActivityDetail = PlatformUserActivitySession & {
  pageVisits: Array<{
    id: string;
    surface: string;
    pageKey: string;
    pageLabel: string;
    startedAt: string;
    lastSeenAt: string;
    endedAt: string | null;
    durationSeconds: number;
    activeSeconds: number;
  }>;
};

export type PlatformUserActivityList = {
  records: PlatformUserActivitySession[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
  summary: { active: number; inactive: number; ended: number; averageActiveSeconds: number };
  filters: { search: string; organizationId: string; userId: string; status: string; from: string; to: string };
  filterOptions: {
    organizations: Array<{ id: string; name: string | null; slug: string | null }>;
    users: Array<{ id: string; username: string; fullName: string | null; email: string | null; organizationIds: string[] }>;
  };
  retentionDays: number;
};

export type PlatformUserActivityFilters = {
  page?: number;
  pageSize?: number;
  search?: string;
  organizationId?: string;
  userId?: string;
  status?: string;
  from?: string;
  to?: string;
};

export type PlatformUserActivityAnalytics = {
  filters: PlatformUserActivityList['filters'];
  totals: {
    sessions: number;
    uniqueUsers: number;
    totalActiveSeconds: number;
    totalSessionSeconds: number;
    averageActiveSessionSeconds: number;
    averageSessionSeconds: number;
  };
  daily: Array<{ date: string; sessions: number; uniqueUsers: number; activeSeconds: number }>;
  pages: Array<{ surface: string; pageKey: string; pageLabel: string; visitCount: number; activeSeconds: number }>;
  users: Array<{
    user: { id: string; username: string; fullName: string | null };
    sessionCount: number;
    activeSeconds: number;
    lastSeenAt: string | null;
  }>;
  organizations: Array<{
    organization: { id: string; name: string | null; slug: string | null };
    sessionCount: number;
    activeSeconds: number;
    lastSeenAt: string | null;
  }>;
};

async function parseResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => null) as T | { error?: string } | null;
  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'error' in payload && payload.error
      ? String(payload.error)
      : 'User activity request failed';
    throw new Error(message);
  }
  return payload as T;
}

export async function fetchPlatformUserActivitySessions(
  token: string,
  filters: PlatformUserActivityFilters = {}
): Promise<PlatformUserActivityList> {
  const base = await getBaseUrl();
  const query = userActivityQuery(filters);
  const response = await fetch(`${base}/api/platform-support/user-activity/sessions?${query}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store'
  });
  return parseResponse<PlatformUserActivityList>(response);
}

function userActivityQuery(filters: PlatformUserActivityFilters = {}) {
  const query = new URLSearchParams();
  if (filters.page) query.set('page', String(filters.page));
  if (filters.pageSize) query.set('pageSize', String(filters.pageSize));
  if (filters.search) query.set('search', filters.search);
  if (filters.organizationId) query.set('organizationId', filters.organizationId);
  if (filters.userId) query.set('userId', filters.userId);
  if (filters.status && filters.status !== 'ALL') query.set('status', filters.status);
  if (filters.from) query.set('from', filters.from);
  if (filters.to) query.set('to', filters.to);
  return query;
}

export async function fetchPlatformUserActivityAnalytics(
  token: string,
  filters: PlatformUserActivityFilters = {}
): Promise<PlatformUserActivityAnalytics> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/user-activity/analytics?${userActivityQuery(filters)}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store'
  });
  const payload = await parseResponse<{ analytics: PlatformUserActivityAnalytics }>(response);
  return payload.analytics;
}

export async function exportPlatformUserActivityCsv(
  token: string,
  filters: PlatformUserActivityFilters = {}
): Promise<{ blob: Blob; filename: string }> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/user-activity/export.csv?${userActivityQuery(filters)}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store'
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(payload?.error || 'User activity export failed');
  }
  const disposition = response.headers.get('content-disposition') || '';
  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || 'bonzaiq-user-activity.csv';
  return { blob: await response.blob(), filename };
}

export async function fetchPlatformUserActivitySession(
  token: string,
  sessionId: string
): Promise<PlatformUserActivityDetail> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/user-activity/sessions/${encodeURIComponent(sessionId)}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store'
  });
  const payload = await parseResponse<{ session: PlatformUserActivityDetail }>(response);
  return payload.session;
}
